import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Row, Col, Typography, Avatar, Tooltip, Spin, Tag } from 'antd';
import {
  MailOutlined,
  TeamOutlined,
  ProjectOutlined,
  CheckSquareOutlined,
  CalendarOutlined,
  CloudOutlined,
  CustomerServiceOutlined,
  ClockCircleOutlined,
  BookOutlined,
  TrophyOutlined,
  EditOutlined,
  IdcardOutlined,
  RocketOutlined,
  FolderOpenOutlined,
  AppstoreOutlined,
  LeftOutlined,
  RightOutlined,
  GiftOutlined,
  ScheduleOutlined,
  DashboardOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import useUrlFilters from 'hooks/useUrlFilters';

const { Title, Text } = Typography;

// Calendar month/day live in the URL (no defaults: the fallback is today, and a link must keep its dates).
const URL_FILTERS = {
  calMonth: { type: 'string', key: 'cal_month' }, // YYYY-MM
  calDay: { type: 'date', key: 'cal_day' },
};

const MyDashboard = ({ employee }) => {
  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [urlFilters, setUrlFilters] = useUrlFilters(URL_FILTERS);
  const calendarDate = useMemo(
    () => (urlFilters.calMonth && dayjs(`${urlFilters.calMonth}-01`).isValid() ? dayjs(`${urlFilters.calMonth}-01`) : dayjs()),
    [urlFilters.calMonth]
  );
  const setCalendarDate = (date) => setUrlFilters({ calMonth: date ? date.format('YYYY-MM') : null });
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [todayEvents, setTodayEvents] = useState([]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const fetchCalendarEvents = useCallback(async (month, year) => {
    setCalendarLoading(true);
    try {
      const response = await axios.get(`/employee/employees/calendar-events/`, {
        params: { month, year },
      });
      if (response.status === 200) {
        setCalendarEvents(response.data);
      }
    } catch (err) {
      console.error('Error fetching calendar events:', err);
    } finally {
      setCalendarLoading(false);
    }
  }, []);

  // Fetch today's events once on mount
  useEffect(() => {
    const today = dayjs();
    axios.get(`/employee/employees/calendar-events/`, {
      params: { month: today.month() + 1, year: today.year() },
    }).then((res) => {
      if (res.status === 200) {
        const todayStr = today.format('YYYY-MM-DD');
        setTodayEvents(res.data.filter((e) => e.date === todayStr));
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    fetchCalendarEvents(calendarDate.month() + 1, calendarDate.year());
  }, [calendarDate, fetchCalendarEvents]);

  const greeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const formatDate = () => {
    return currentTime.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = () => {
    return currentTime.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleClick = (link) => {
    if (link.external) {
      window.open(link.url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(link.url);
    }
  };

  const externalTools = [
    {
      title: 'Outlook Mail',
      icon: <MailOutlined />,
      url: 'https://outlook.office.com/mail/',
      external: true,
      color: '#0078d4',
      bg: 'linear-gradient(135deg, #0078d4 0%, #00bcf2 100%)',
      description: 'Check your emails',
    },
    {
      title: 'MS Teams',
      icon: <TeamOutlined />,
      url: 'https://teams.microsoft.com/',
      external: true,
      color: '#6264a7',
      bg: 'linear-gradient(135deg, #6264a7 0%, #8b8cc7 100%)',
      description: 'Chat & meetings',
    },
    {
      title: 'Calendar',
      icon: <CalendarOutlined />,
      url: 'https://outlook.office.com/calendar/view/month',
      external: true,
      color: '#0078d4',
      bg: 'linear-gradient(135deg, #0078d4 0%, #50e6ff 100%)',
      description: 'View your schedule',
    },
    {
      title: 'OneDrive',
      icon: <CloudOutlined />,
      url: 'https://onedrive.live.com/',
      external: true,
      color: '#0078d4',
      bg: 'linear-gradient(135deg, #0078d4 0%, #28a8ea 100%)',
      description: 'Cloud file storage',
    },
    {
      title: 'Jenkins',
      icon: <RocketOutlined />,
      url: 'https://jenkins.teosn.xyz',
      external: true,
      color: '#d33833',
      bg: 'linear-gradient(135deg, #d33833 0%, #ef5350 100%)',
      description: 'CI/CD pipelines',
    },
    {
      title: 'SharePoint',
      icon: <FolderOpenOutlined />,
      url: 'https://www.office.com/launch/sharepoint',
      external: true,
      color: '#038387',
      bg: 'linear-gradient(135deg, #038387 0%, #00b294 100%)',
      description: 'Team documents',
    },
    {
      title: 'Uptime',
      icon: <DashboardOutlined />,
      url: 'https://uptime.teson.xyz',
      external: true,
      color: '#389e0d',
      bg: 'linear-gradient(135deg, #389e0d 0%, #73d13d 100%)',
      description: 'Service uptime monitor',
    },
  ];

  const workLinks = [
    {
      title: 'Project Board',
      icon: <ProjectOutlined />,
      url: '/projects/overview',
      external: false,
      color: '#722ed1',
      bg: 'linear-gradient(135deg, #722ed1 0%, #b37feb 100%)',
      description: 'View all projects',
    },
    {
      title: 'My Tasks',
      icon: <CheckSquareOutlined />,
      url: '/tasks',
      external: false,
      color: '#13c2c2',
      bg: 'linear-gradient(135deg, #13c2c2 0%, #5cdbd3 100%)',
      description: 'Track your tasks',
    },
    {
      title: 'Daily Updates',
      icon: <EditOutlined />,
      url: '/myprofile',
      external: false,
      color: '#fa8c16',
      bg: 'linear-gradient(135deg, #fa8c16 0%, #ffc53d 100%)',
      description: 'Submit daily updates',
    },
    {
      title: 'Support Tickets',
      icon: <CustomerServiceOutlined />,
      url: '/support/tickets',
      external: false,
      color: '#eb2f96',
      bg: 'linear-gradient(135deg, #eb2f96 0%, #ff85c0 100%)',
      description: 'Raise or view tickets',
    },
    {
      title: 'Knowledge Base',
      icon: <BookOutlined />,
      url: '/support/knowledge-base',
      external: false,
      color: '#52c41a',
      bg: 'linear-gradient(135deg, #52c41a 0%, #95de64 100%)',
      description: 'Articles & guides',
    },
  ];

  const hrLinks = [
    {
      title: 'Attendance',
      icon: <ClockCircleOutlined />,
      url: '/attendance',
      external: false,
      color: '#2f54eb',
      bg: 'linear-gradient(135deg, #2f54eb 0%, #85a5ff 100%)',
      description: 'Mark attendance',
    },
    {
      title: 'Leave',
      icon: <CalendarOutlined />,
      url: '/leave-management',
      external: false,
      color: '#fa541c',
      bg: 'linear-gradient(135deg, #fa541c 0%, #ff9c6e 100%)',
      description: 'Apply for leave',
    },
    {
      title: 'Payslips',
      icon: <ScheduleOutlined />,
      url: '/myprofile',
      external: false,
      color: '#52c41a',
      bg: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
      description: 'View salary slips',
    },
    {
      title: 'Performance',
      icon: <TrophyOutlined />,
      url: '/hr/performance-reviews',
      external: false,
      color: '#f5222d',
      bg: 'linear-gradient(135deg, #f5222d 0%, #ff7875 100%)',
      description: 'Review performance',
    },
  ];

  // Calendar helpers
  const selectedDate = urlFilters.calDay || dayjs();
  const setSelectedDate = (date) => setUrlFilters({ calDay: date });

  const eventTypeConfig = {
    holiday: { color: '#f5222d', label: 'Holiday', icon: <CalendarOutlined /> },
    birthday: { color: '#722ed1', label: 'Birthday', icon: <GiftOutlined /> },
    leave: { color: '#fa8c16', label: 'Leave', icon: <ClockCircleOutlined /> },
    event: { color: '#52c41a', label: 'Event', icon: <CalendarOutlined /> },
    outlook: { color: '#13c2c2', label: 'Outlook', icon: <CalendarOutlined /> },
  };

  // Build a lookup map for fast date access
  const eventsMap = useMemo(() => {
    const map = {};
    calendarEvents.forEach((e) => {
      if (!map[e.date]) map[e.date] = [];
      map[e.date].push(e);
    });
    return map;
  }, [calendarEvents]);

  const getEventsForDate = (dateStr) => eventsMap[dateStr] || [];

  // Build calendar grid
  const calendarGrid = useMemo(() => {
    const startOfMonth = calendarDate.startOf('month');
    const endOfMonth = calendarDate.endOf('month');
    const startDay = startOfMonth.day(); // 0=Sun
    const daysInMonth = endOfMonth.date();

    // Previous month fill
    const prevMonthEnd = startOfMonth.subtract(1, 'day');
    const cells = [];
    for (let i = startDay - 1; i >= 0; i--) {
      cells.push({ date: prevMonthEnd.subtract(i, 'day'), currentMonth: false });
    }
    // Current month
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ date: calendarDate.date(d), currentMonth: true });
    }
    // Next month fill
    const remaining = 42 - cells.length; // 6 rows
    for (let i = 1; i <= remaining; i++) {
      cells.push({ date: endOfMonth.add(i, 'day'), currentMonth: false });
    }
    return cells;
  }, [calendarDate]);

  const goToPrevMonth = () => {
    const prev = calendarDate.subtract(1, 'month');
    setCalendarDate(prev);
  };

  const goToNextMonth = () => {
    const next = calendarDate.add(1, 'month');
    setCalendarDate(next);
  };

  const goToToday = () => {
    setCalendarDate(dayjs());
    setSelectedDate(dayjs());
  };

  const selectedDateEvents = getEventsForDate(selectedDate.format('YYYY-MM-DD'));


  const renderLinkItem = (link) => (
    <div
      className="dashboard-link-item"
      key={link.title}
      onClick={() => handleClick(link)}
    >
      <Tooltip title={link.description} placement="right">
        <div className="dashboard-link-item-inner">
          <div className="dashboard-link-icon" style={{ background: link.bg }}>
            {React.cloneElement(link.icon, { style: { fontSize: 18, color: '#fff' } })}
          </div>
          <Text className="dashboard-link-title">{link.title}</Text>
          {link.external && (
            <span className="dashboard-external-badge">EXT</span>
          )}
        </div>
      </Tooltip>
    </div>
  );

  const renderLinkSection = (title, links, icon) => (
    <div className="dashboard-link-section">
      <div className="dashboard-link-section-header">
        {React.cloneElement(icon, { style: { fontSize: 14, color: '#0050b3' } })}
        <Text strong style={{ fontSize: 13 }}>{title}</Text>
      </div>
      {links.map(renderLinkItem)}
    </div>
  );

  const firstName = employee
    ? (employee.user?.first_name || '')
    : user?.first_name || user?.username || 'there';


  return (
    <div className="my-dashboard-container">
      {/* Welcome Banner */}
      <div className="dashboard-welcome-banner">
        <div className="dashboard-welcome-content">
          <div className="dashboard-welcome-text">
            <Title level={3} style={{ color: '#fff', margin: 0 }}>
              {greeting()}, {firstName}!
            </Title>
            <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 15 }}>
              {formatDate()} &middot; {formatTime()}
            </Text>
          </div>
          <Avatar
            size={56}
            style={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              border: '2px solid rgba(255,255,255,0.4)',
              fontSize: 24,
              fontWeight: 600,
            }}
          >
            {firstName.charAt(0).toUpperCase()}
          </Avatar>
        </div>
      </div>

      {/* Main Layout: Links (30%) | Calendar (70%) */}
      <Row gutter={24} className="dashboard-main-row">
        {/* Left: Quick Links */}
        <Col xs={24} lg={7} className="dashboard-links-col">
          <div className="dashboard-links-panel">
            {todayEvents.length > 0 && (
              <div className="dashboard-link-section dashboard-today-section">
                <div className="dashboard-link-section-header">
                  <CalendarOutlined style={{ fontSize: 14, color: '#0050b3' }} />
                  <Text strong style={{ fontSize: 13 }}>Today's Events</Text>
                  <Tag color="blue" style={{ marginLeft: 'auto', fontSize: 10, lineHeight: '16px', padding: '0 6px' }}>{todayEvents.length}</Tag>
                </div>
                {todayEvents.map((evt, idx) => {
                  const cfg = eventTypeConfig[evt.type] || { color: '#1890ff', icon: <CalendarOutlined /> };
                  const timeStr = evt.start_time
                    ? dayjs(evt.start_time, 'HH:mm:ss').format('h:mm A')
                    : '';
                  return (
                    <div key={idx} className="dashboard-today-item">
                      <div className="dashboard-today-dot" style={{ background: cfg.color }} />
                      <div className="dashboard-today-info">
                        <Text className="dashboard-today-title">{evt.title}</Text>
                        {timeStr && <Text className="dashboard-today-time">{timeStr}{evt.end_time ? ` - ${dayjs(evt.end_time, 'HH:mm:ss').format('h:mm A')}` : ''}</Text>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {renderLinkSection('External Tools', externalTools, <AppstoreOutlined />)}
            {renderLinkSection('Work & Projects', workLinks, <ProjectOutlined />)}
            {renderLinkSection('HR & Admin', hrLinks, <IdcardOutlined />)}
          </div>
        </Col>

        {/* Right: Calendar */}
        <Col xs={24} lg={17} className="dashboard-calendar-col">
          <div className="mcal-panel">
            {/* Calendar Header */}
            <div className="mcal-header">
              <div className="mcal-header-left">
                <button className="mcal-nav-btn" onClick={goToPrevMonth}><LeftOutlined /></button>
                <h3 className="mcal-month-title">{calendarDate.format('MMMM YYYY')}</h3>
                <button className="mcal-nav-btn" onClick={goToNextMonth}><RightOutlined /></button>
              </div>
              <div className="mcal-header-right">
                <button className="mcal-today-btn" onClick={goToToday}>Today</button>
                <div className="mcal-legend">
                  {Object.entries(eventTypeConfig).map(([type, cfg]) => (
                    <span key={type} className="mcal-legend-item">
                      <span className="mcal-legend-dot" style={{ background: cfg.color }} />
                      {cfg.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <Spin spinning={calendarLoading}>
              <div className="mcal-body">
                {/* Weekday Headers */}
                <div className="mcal-weekdays">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                    <div key={d} className="mcal-weekday">{d}</div>
                  ))}
                </div>

                {/* Date Grid */}
                <div className="mcal-grid">
                  {calendarGrid.map((cell, idx) => {
                    const dateStr = cell.date.format('YYYY-MM-DD');
                    const events = getEventsForDate(dateStr);
                    const isToday = cell.date.isSame(dayjs(), 'day');
                    const isSelected = cell.date.isSame(selectedDate, 'day');
                    const hasHoliday = events.some((e) => e.type === 'holiday');

                    return (
                      <div
                        key={idx}
                        className={[
                          'mcal-cell',
                          !cell.currentMonth && 'mcal-cell-outside',
                          isToday && 'mcal-cell-today',
                          isSelected && 'mcal-cell-selected',
                          hasHoliday && cell.currentMonth && 'mcal-cell-holiday',
                        ].filter(Boolean).join(' ')}
                        onClick={() => {
                          setSelectedDate(cell.date);
                          if (!cell.currentMonth) setCalendarDate(cell.date.startOf('month').date(1));
                        }}
                      >
                        <span className="mcal-date-num">{cell.date.date()}</span>
                        {cell.currentMonth && events.length > 0 && (
                          <div className="mcal-cell-events">
                            {events.slice(0, 3).map((evt, eIdx) => {
                              const ecfg = eventTypeConfig[evt.type] || { color: '#1890ff' };
                              const timeStr = evt.start_time
                                ? dayjs(evt.start_time, 'HH:mm:ss').format('h:mm A')
                                : '';
                              return (
                                <div
                                  key={eIdx}
                                  className="mcal-cell-evt"
                                  style={{ '--evt-c': ecfg.color }}
                                >
                                  <span className="mcal-cell-evt-dot" style={{ background: ecfg.color }} />
                                  <span className="mcal-cell-evt-text">
                                    {timeStr ? `${timeStr} ` : ''}{evt.title}
                                  </span>
                                </div>
                              );
                            })}
                            {events.length > 3 && (
                              <span className="mcal-cell-more">+{events.length - 3} more</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </Spin>

            {/* Selected Day Events Panel */}
            <div className="mcal-events-panel">
              <div className="mcal-events-panel-header">
                <CalendarOutlined />
                <span>{selectedDate.format('dddd, MMM D, YYYY')}</span>
              </div>
              {selectedDateEvents.length === 0 ? (
                <div className="mcal-no-events">No events on this day</div>
              ) : (
                <div className="mcal-events-list">
                  {selectedDateEvents.map((evt, idx) => {
                    const cfg = eventTypeConfig[evt.type] || { color: '#1890ff', icon: <CalendarOutlined /> };
                    return (
                      <div key={idx} className="mcal-event-item" style={{ '--evt-color': cfg.color }}>
                        <div className="mcal-event-icon" style={{ background: cfg.color }}>
                          {cfg.icon && React.cloneElement(cfg.icon, { style: { color: '#fff', fontSize: 12 } })}
                        </div>
                        <div className="mcal-event-info">
                          <span className="mcal-event-title">{evt.title}</span>
                          <span className="mcal-event-type">
                            {cfg.label}
                            {evt.start_time && (
                              <span className="mcal-event-time">
                                {dayjs(evt.start_time, 'HH:mm:ss').format('h:mm A')}
                                {evt.end_time && ` - ${dayjs(evt.end_time, 'HH:mm:ss').format('h:mm A')}`}
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default MyDashboard;
